/**
 * The Curry Creations — custom theme JavaScript
 * ============================================================
 * [1] WEEKLY MENU DISH MODAL           (Aug 9 2026, rev 4)
 * [2] PORTAL STATUS MESSAGE CORRECTION (Aug 11 2026, rev 2 — Sun 11PM rollover)
 * [3] PORTAL DISH-COUNT ENFORCEMENT    (Aug 11 2026, rev 2 — freeze fixed)
 * [4] RESUME SUBSCRIPTION BUTTON       (Aug 11 2026)
 *
 * [5] CUTOFF TEXT SYNC was REMOVED on Aug 11 2026. It existed only to rewrite
 * a stale "Thursday 11PM" string from outside. That string lived in
 * templates/page.tcc-dish-portal.liquid (1.5KB) — NOT the 74KB snippet — and
 * has been corrected at source, so the module was masking a fix that no longer
 * needs masking and acting as a second source of truth for the cutoff.
 *
 * ⚠️ weekOfMonday() in [2] MUST stay logically equivalent to tccWeekOfMonday_()
 * in snippets/tcc-dish-portal.liquid. They are two implementations of one key.
 * If they disagree, [2] queries a different week than the one the snippet
 * wrote and shows false "no order" alerts to healthy subscribers. Both roll
 * over at SUNDAY 11PM ET — see the long note above weekOfMonday() for why that
 * is not the same instant as the Friday 8PM order cutoff.
 *
 * ⚠️ STANDING RULE FOR THIS FILE — every MutationObserver here is scoped to
 * document.body with subtree:true, and every callback WRITES to the DOM. That
 * is a feedback loop by construction. Two defences are applied to all three,
 * and any new observer added to this file must use both:
 *
 *   1. COALESCE through requestAnimationFrame. An observer callback is a
 *      MICROTASK, so a write-triggers-callback-triggers-write cycle drains the
 *      microtask queue forever WITHOUT ever yielding to paint — that is the
 *      "Page Unresponsive" dialog. Deferring to rAF caps the cycle at one pass
 *      per frame, so even a genuine loop degrades to 60fps churn and the page
 *      stays alive and debuggable.
 *   2. MAKE EVERY WRITE IDEMPOTENT. Compare before assigning. Assigning the
 *      value a node already has still emits a mutation record.
 *
 * Defence 2 stops the loop; defence 1 stops a missed case from being fatal.
 * ============================================================
 */

/* ============================================================
 * [1] WEEKLY MENU DISH MODAL
 *
 * Product-page dish grid (.wm-card) shows image + name only. Descriptions live
 * in the `weekly_menu` metaobject and were never output there; the allergen
 * badge revealed its text on :hover ONLY, so allergen info was invisible on
 * every touchscreen. Tapping a card opens a modal with description AND allergen.
 *
 * Lives here rather than sections/product-template.liquid because that file is
 * 46KB and theme writes require submitting the whole file.
 *
 * ⚠️ DO NOT SEND `Accept: application/json` TO A SHOPIFY PAGE URL.
 * Shopify then returns ITS OWN page object - {"page":{"id":...}} - with HTTP
 * 200 and content-type application/json, INSTEAD of rendering our Liquid
 * template. It parses fine and has no `dishes` key. Status and content-type
 * are IDENTICAL for both; only the BODY differs. Cost four revisions.
 * LESSON: when comparing a working request against a failing one, diff the
 * response BODY, not its metadata.
 *
 * Cards are wired immediately and the feed is fetched lazily ON CLICK, so
 * nothing depends on a network call completing during page load.
 * ============================================================ */
(function () {
  'use strict';

  var CARD = '.wm-card';
  var cache = null;

  function norm(s) {
    return (s || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function getDishes() {
    if (cache) return Promise.resolve(cache);
    return fetch('/pages/menu-feed')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (txt) {
        var data = JSON.parse(txt);
        var map = {};
        (data.dishes || []).forEach(function (d) { map[norm(d.name)] = d; });
        if (!Object.keys(map).length) throw new Error('feed parsed but contained no dishes');
        cache = map;
        return cache;
      });
  }

  function injectStyles() {
    if (document.getElementById('tcc-dish-modal-css')) return;
    var css = document.createElement('style');
    css.id = 'tcc-dish-modal-css';
    css.textContent = [
      '.wm-card[data-tcc-dm="1"]{cursor:pointer;position:relative}',
      '.wm-card:focus-visible{outline:2px solid #A52020;outline-offset:2px}',
      '.tcc-dm-hint{position:absolute;bottom:6px;right:8px;font-size:15px;line-height:1;color:#A52020;opacity:.65;pointer-events:none}',
      '.tcc-dm-overlay{position:fixed;inset:0;background:rgba(61,31,31,.55);display:flex;align-items:center;justify-content:center;padding:20px;z-index:2147483000;opacity:0;transition:opacity .18s ease}',
      '.tcc-dm-overlay.is-open{opacity:1}',
      '.tcc-dm{background:#fff;border:1px solid #EDD9C0;border-radius:16px;max-width:380px;width:100%;max-height:88vh;overflow-y:auto;position:relative;box-shadow:0 18px 50px rgba(0,0,0,.28);transform:translateY(8px);transition:transform .18s ease}',
      '.tcc-dm-overlay.is-open .tcc-dm{transform:translateY(0)}',
      '.tcc-dm-close{position:absolute;top:10px;right:10px;width:34px;height:34px;border-radius:50%;border:1px solid #EDD9C0;background:rgba(255,255,255,.94);color:#3D1F1F;font-size:19px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;z-index:2}',
      '.tcc-dm-close:hover{background:#FFF8F0;border-color:#A52020;color:#A52020}',
      '.tcc-dm-img{aspect-ratio:4/3;background:#FDF6EE;border-radius:16px 16px 0 0;overflow:hidden}',
      '.tcc-dm-img img{width:100%;height:100%;object-fit:contain;padding:18px;display:block;box-sizing:border-box}',
      '.tcc-dm-body{padding:16px 18px 20px}',
      '.tcc-dm-badges{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}',
      '.tcc-dm-badge{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;padding:4px 11px;border-radius:12px}',
      '.tcc-dm-dot{width:11px;height:11px;border-radius:50%;flex-shrink:0}',
      '.tcc-b-veg{background:#EAF3DE;color:#3B6D11}.tcc-d-veg{background:#2E7D32;border:2px solid #1B5E20}',
      '.tcc-b-nonveg{background:#FCEBEB;color:#A32D2D}.tcc-d-nonveg{background:#B71C1C;border:2px solid #7F0000}',
      '.tcc-b-safe{background:#E8F5E9;color:#2E7D32}.tcc-d-safe{background:#4CAF50;border:2px solid #2E7D32}',
      '.tcc-b-allergen{background:#FBE9E7;color:#E65100}.tcc-d-allergen{background:#E65100;border:2px solid #BF360C}',
      '.tcc-dm-title{font-family:Nunito,sans-serif;font-size:18px;font-weight:800;color:#3D1F1F;margin:0 0 8px}',
      '.tcc-dm-desc{font-family:"DM Sans",sans-serif;font-size:14px;color:#8C6A5A;line-height:1.7;margin:0}',
      'body.tcc-dm-lock{overflow:hidden}'
    ].join('');
    document.head.appendChild(css);
  }

  var overlay = null;
  var lastFocused = null;

  function closeModal() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.classList.remove('tcc-dm-lock');
    setTimeout(function () {
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      overlay = null;
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }, 180);
  }

  function makeBadge(text, kind) {
    var s = document.createElement('span');
    s.className = 'tcc-dm-badge tcc-b-' + kind;
    var dot = document.createElement('span');
    dot.className = 'tcc-dm-dot tcc-d-' + kind;
    s.appendChild(dot);
    s.appendChild(document.createTextNode(text));
    return s;
  }

  function buildModal(dish, fallbackName, errText) {
    closeModal();
    lastFocused = document.activeElement;
    dish = dish || {};

    var isVeg = norm(dish.type) === 'veg';
    var safe = norm(dish.allergen).indexOf('no added') === 0;

    overlay = document.createElement('div');
    overlay.className = 'tcc-dm-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', dish.name || fallbackName || 'Dish details');

    var box = document.createElement('div');
    box.className = 'tcc-dm';

    var btn = document.createElement('button');
    btn.className = 'tcc-dm-close';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Close');
    btn.innerHTML = '&times;';
    btn.addEventListener('click', closeModal);
    box.appendChild(btn);

    if (dish.image) {
      var imgWrap = document.createElement('div');
      imgWrap.className = 'tcc-dm-img';
      var img = document.createElement('img');
      img.src = dish.image;
      img.alt = dish.name || '';
      imgWrap.appendChild(img);
      box.appendChild(imgWrap);
    }

    var body = document.createElement('div');
    body.className = 'tcc-dm-body';

    if (dish.type || dish.allergen) {
      var badges = document.createElement('div');
      badges.className = 'tcc-dm-badges';
      if (dish.type) { badges.appendChild(makeBadge(dish.type, isVeg ? 'veg' : 'nonveg')); }
      if (dish.allergen) { badges.appendChild(makeBadge(dish.allergen, safe ? 'safe' : 'allergen')); }
      body.appendChild(badges);
    }

    var h = document.createElement('h3');
    h.className = 'tcc-dm-title';
    h.textContent = dish.name || fallbackName || '';
    body.appendChild(h);

    var p = document.createElement('p');
    p.className = 'tcc-dm-desc';
    p.textContent = errText || dish.description || '';
    body.appendChild(p);

    box.appendChild(body);
    overlay.appendChild(box);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });

    document.body.appendChild(overlay);
    document.body.classList.add('tcc-dm-lock');
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });
    btn.focus();
  }

  function openFor(name) {
    getDishes()
      .then(function (map) {
        var dish = map[norm(name)];
        if (dish) { buildModal(dish, name, null); }
        else { buildModal(null, name, 'Description not available for this dish yet.'); }
      })
      .catch(function (err) {
        console.error('[TCC] dish feed failed on click:', err.message);
        buildModal(null, name, 'Could not load the description just now. Please try again in a moment.');
      });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') closeModal();
  });

  function wireCards() {
    var cards = document.querySelectorAll(CARD);
    if (!cards.length) return 0;
    injectStyles();

    Array.prototype.forEach.call(cards, function (card) {
      if (card.getAttribute('data-tcc-dm') === '1') return;
      var nameEl = card.querySelector('.wm-name');
      if (!nameEl) return;
      var name = nameEl.textContent.trim();

      card.setAttribute('data-tcc-dm', '1');
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', name + ' \u2014 view description');

      if (!card.querySelector('.tcc-dm-hint')) {
        var hint = document.createElement('span');
        hint.className = 'tcc-dm-hint';
        hint.setAttribute('aria-hidden', 'true');
        hint.textContent = '\u24D8';
        card.appendChild(hint);
      }

      card.addEventListener('click', function () { openFor(name); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFor(name); }
      });
    });

    return document.querySelectorAll(CARD + '[data-tcc-dm="1"]').length;
  }

  window.tccWireDishCards = wireCards;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireCards);
  } else {
    wireCards();
  }
  window.addEventListener('load', wireCards);
  setTimeout(wireCards, 800);
  setTimeout(wireCards, 2000);
  setTimeout(wireCards, 5000);

  // wireCards() APPENDS a hint span and SETS attributes, both of which are
  // mutations this observer would see. The data-tcc-dm guard makes the second
  // pass a no-op so it terminates, but on a mutation-heavy page (the dish
  // portal re-renders constantly) it still ran a whole-document querySelectorAll
  // per mutation. Coalesced to one pass per frame.
  if (window.MutationObserver && document.body) {
    var cardsQueued = false;
    new MutationObserver(function () {
      if (cardsQueued) return;
      cardsQueued = true;
      requestAnimationFrame(function () { cardsQueued = false; wireCards(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();


/* ============================================================
 * [2] PORTAL SUBSCRIPTION STATUS  (rev 6 — Aug 24 2026)
 *
 * REPLACES module [2] in assets/custom.js in its entirety.
 * Requires the companion Liquid patch in snippets/tcc-dish-portal.liquid
 * (customer.tags parsing + data-tag-sub-status on #tcc-portal).
 *
 * ---------------------------------------------------------------------------
 * WHAT CHANGED FROM REV 5
 *
 * rev 5 handled ACTIVE / PAUSED / CANCELLED. Shopify's
 * SubscriptionContractSubscriptionStatus enum has FIVE values — FAILED and
 * EXPIRED were missing, so they fell through to the final else and rendered
 * NOTHING. A subscriber whose card had hard-failed saw a portal identical to
 * an untagged customer's: no banner, no explanation, no way to act. That is
 * the one case where saying nothing is genuinely worse than saying something,
 * because it is recoverable and the customer is the only one who can recover it.
 *
 * The companion Liquid renumbered its rank scale to 5..1 for the same reason:
 *   ACTIVE(5) > PAUSED(4) > FAILED(3) > EXPIRED(2) > CANCELLED(1)
 * ACTIVE still outranks FAILED deliberately — a customer holding one working
 * plan and one dead one must not be told their subscription failed.
 *
 * ---------------------------------------------------------------------------
 * WHAT CHANGED IN REV 5, AND WHY (retained)
 *
 * rev 4 read subscription_status from the Worker, which read it from the
 * Airtable SubscriptionContracts mirror — a table that had drifted three
 * times in five weeks (Jul 21, Aug 10, Aug 23 manual imports) because
 * nothing kept it in sync with Shopify.
 *
 * The fix isn't a better mirror. It's not needing one. Flow can write
 * customer tags on Shopify Basic (confirmed live: "Send HTTP request" is
 * Plus-only and greyed out on this store; "Add/Remove customer tags" is
 * not). Workflow "Sync contract status to customer tags" writes
 * sub:<contract_id>:<STATUS> onto the customer on every contract change.
 * Liquid reads customer.tags for free on every page render and passes the
 * resolved status through data-tag-sub-status.
 *
 * ⚠️ Flow fires on CHANGE only. The 32 contracts existing as of 2026-08-24
 * were backfilled by hand via tagsAdd. A contract that reaches FAILED or
 * EXPIRED only produces a tag if the workflow branches on those values —
 * verify that before trusting this module to cover them.
 *
 * has_order and confirmed still come from the Worker's /api/portal/status —
 * genuinely order data (does a row exist in Airtable Orders for this week),
 * which tags don't carry and never should. It is ONLY fetched when tag status
 * is ACTIVE; every other state's message is independent of order presence.
 *
 * ---------------------------------------------------------------------------
 * KNOWN LIMIT, STATED PLAINLY RATHER THAN DISCOVERED LATER
 *
 * customer.tags is rendered by Liquid at PAGE LOAD. Unlike has_order (polled
 * every 60s), a pause or cancel will NOT flip the banner in an already-open
 * tab — the tag is baked into HTML that already downloaded. What updates it:
 *   - any fresh page load / hard refresh
 *   - clicking Resume/Restart/Update-payment here, which forces a reload on
 *     return from Shopify's account portal (click handler on the CTA below) —
 *     the one moment a status change must be seen immediately, and it's covered
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS MODULE EXISTS TO ENFORCE, UNCHANGED SINCE REV 3
 * NEVER RENDER A NEGATIVE CLAIM FROM ABSENT DATA. Unknown tag status (no
 * sub: tag matched — including a customer logged in under one of the
 * deliberately-unmerged duplicate Shopify records) renders NOTHING.
 * ============================================================ */
(function () {
  'use strict';

  var portal = document.getElementById('tcc-portal');
  if (!portal || portal.dataset.subscriber !== 'true') return;

  var WORKER_URL = 'https://tcc-kitchen-worker.long-rice-fd6e.workers.dev';
  var email = portal.dataset.customerEmail;

  // '' | 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'FAILED' | 'EXPIRED' — written by
  // the Liquid patch. These are all five values of Shopify's
  // SubscriptionContractSubscriptionStatus enum. Anything else is unknown.
  var tagStatus = (portal.dataset.tagSubStatus || '').toString().trim().toUpperCase();

  var SHOW_CONTRACT_IDS = true; // kept from rev 4; tags don't expose per-contract IDs to JS, so this is currently inert — see note near contractLine()

  var POLL_MS = 60000;

  // 'ok' | 'pending_saved' | 'pending_pick' | 'paused' | 'cancelled'
  //      | 'failed' | 'expired' | 'unknown'
  var state = 'unknown';
  var confirmed = false;
  var inFlight = false;
  var observerStarted = false;

  /* ---------- copy + tone ---------- */

  var WHATSAPP = '+1 (848) 226-8666';

  var COPY = {
    pending_saved: {
      tone: 'info',
      title: 'Your picks are saved',
      body: "Your subscription is active. This week's order is created automatically on Monday morning and your dishes are applied to it then. You can change them any time before Thursday 11PM."
    },
    pending_pick: {
      tone: 'note',
      title: 'Pick your dishes for this week',
      body: "Your subscription is active and this week's order is created automatically on Monday morning. Pick your dishes before Thursday 11PM \u2014 if you don't, our chefs pick for you."
    },
    paused: {
      tone: 'warn',
      title: 'Your subscription is paused',
      body: "You can still pick dishes, but nothing will be delivered while your subscription is paused. Resume it to get this week's food, or WhatsApp us on " + WHATSAPP + '.'
    },
    cancelled: {
      tone: 'bad',
      title: 'Your subscription is cancelled',
      body: "You can still pick dishes, but nothing will be delivered. Restart your subscription to get this week's food, or WhatsApp us on " + WHATSAPP + '.'
    },
    failed: {
      tone: 'fail',
      title: "Your last payment didn't go through",
      body: "Your subscription stopped because a payment failed, so nothing is scheduled for delivery. Update your payment method to start it up again, or WhatsApp us on " + WHATSAPP + " and we'll sort it out."
    },
    expired: {
      tone: 'warn',
      title: 'Your subscription has ended',
      body: "This plan has run its course, so nothing is scheduled for delivery. Start a new plan to get this week's food, or WhatsApp us on " + WHATSAPP + '.'
    }
  };

  var TONES = {
    info: { bg: '#EAF3DE', border: '#3B6D11', head: '#2E5A0C', text: '#3B6D11', badge: '#3B6D11', glyph: '\u2713' },
    note: { bg: '#EAF3DE', border: '#3B6D11', head: '#2E5A0C', text: '#3B6D11', badge: '#3B6D11', glyph: '\u2192' },
    warn: { bg: '#FFF4E5', border: '#E65100', head: '#B34700', text: '#7A4100', badge: '#E65100', glyph: '!' },
    bad:  { bg: '#FDECEA', border: '#D93025', head: '#B3261E', text: '#7A1F17', badge: '#D93025', glyph: '\u2715' },
    // Same red as `bad` — a failed payment is equally urgent — but a warning
    // glyph rather than a cross, because this state is recoverable and a cross
    // reads as "we ended this".
    fail: { bg: '#FDECEA', border: '#D93025', head: '#B3261E', text: '#7A1F17', badge: '#D93025', glyph: '\u26A0' }
  };

  function ctaLabel() {
    if (state === 'failed') return '\u25B6 Update my payment method';
    if (state === 'cancelled' || state === 'expired') return '\u25B6 Restart my subscription';
    return '\u25B6 Resume my subscription';
  }

  function wantsCta() {
    return state === 'paused' || state === 'cancelled' || state === 'failed' || state === 'expired';
  }

  // rev 4 showed per-contract IDs here when a customer held 2+. Customer
  // tags carry contract_id embedded (sub:<id>:<STATUS>) but this module
  // only reads the RESOLVED status from data-tag-sub-status, not the raw
  // tag list, so per-contract detail isn't available client-side without
  // re-parsing portal's own tags — which Liquid already did once. Left as
  // a no-op rather than re-deriving; revisit if the multi-contract detail
  // is wanted again.
  function contractLine() { return ''; }

  /* ---------- account URL ---------- */

  function accountUrl() {
    var pause = document.querySelector('.tcc-manage-btn--pause');
    return pause ? pause.getAttribute('href') : null;
  }

  function makeResumeCta(label) {
    var url = accountUrl();
    if (!url) return null;
    var a = document.createElement('a');
    a.className = 'tcc-resume-cta';
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = label;
    a.style.cssText = 'display:inline-block;margin-top:12px;background:' + TONES[COPY[state].tone].badge +
      ';color:#fff;padding:11px 20px;border-radius:9px;font-weight:800;text-decoration:none;font-size:.92rem;';
    // Status lives in a Liquid-rendered data attribute baked in at page
    // load, so a fetch on return can't pick up a change made in the Shopify
    // tab that just opened. A reload is what actually re-renders it.
    a.addEventListener('click', function () {
      setTimeout(function () { window.location.reload(); }, 1500);
    });
    return a;
  }

  /* ---------- reversible block rewriting (unchanged from rev 4) ---------- */

  function captureOriginal(block) {
    if (block.getAttribute('data-tcc-captured') === '1') return;
    var h = block.querySelector('.tcc-success__content strong');
    var b = block.querySelector('.tcc-success__content p');
    var icon = block.querySelector('.tcc-success__icon');
    block.setAttribute('data-tcc-captured', '1');
    block.setAttribute('data-tcc-orig-head', h ? h.textContent : '');
    block.setAttribute('data-tcc-orig-body', b ? b.textContent : '');
    block.setAttribute('data-tcc-orig-bg', block.style.background || '');
    block.setAttribute('data-tcc-orig-bc', block.style.borderColor || '');
    if (icon) block.setAttribute('data-tcc-orig-icon', icon.innerHTML);
  }

  function restoreBlock(block) {
    if (!block || block.getAttribute('data-tcc-captured') !== '1') return;
    if (!block.getAttribute('data-tcc-applied')) return;
    var h = block.querySelector('.tcc-success__content strong');
    var b = block.querySelector('.tcc-success__content p');
    var icon = block.querySelector('.tcc-success__icon');
    if (h) { h.textContent = block.getAttribute('data-tcc-orig-head') || ''; h.style.color = ''; }
    if (b) { b.textContent = block.getAttribute('data-tcc-orig-body') || ''; b.style.color = ''; }
    if (icon && block.hasAttribute('data-tcc-orig-icon')) icon.innerHTML = block.getAttribute('data-tcc-orig-icon');
    block.style.background = block.getAttribute('data-tcc-orig-bg') || '';
    block.style.borderColor = block.getAttribute('data-tcc-orig-bc') || '';
    var old = block.querySelector('.tcc-resume-cta');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    block.removeAttribute('data-tcc-applied');
  }

  function applyBlock(block) {
    if (!block) return;
    if (state === 'ok' || state === 'unknown') { restoreBlock(block); return; }
    captureOriginal(block);
    if (block.getAttribute('data-tcc-applied') === state) { ensureCta(block); return; }
    restoreBlock(block);

    var copy = COPY[state], tone = TONES[copy.tone];

    var icon = block.querySelector('.tcc-success__icon');
    if (icon) {
      icon.innerHTML = '';
      var badge = document.createElement('span');
      badge.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;' +
        'width:36px;height:36px;border-radius:50%;background:' + tone.badge + ';color:#fff;' +
        'font-size:21px;font-weight:700;line-height:1;';
      badge.textContent = tone.glyph;
      icon.appendChild(badge);
    }

    var heading = block.querySelector('.tcc-success__content strong');
    if (heading) { heading.textContent = copy.title; heading.style.color = tone.head; }

    var body = block.querySelector('.tcc-success__content p');
    if (body) { body.textContent = copy.body; body.style.color = tone.text; }

    block.style.background = tone.bg;
    block.style.borderColor = tone.border;
    block.setAttribute('data-tcc-applied', state);
    ensureCta(block);
  }

  function ensureCta(block) {
    if (!block || !wantsCta()) return;
    if (block.querySelector('.tcc-resume-cta')) return;
    var host = block.querySelector('.tcc-success__content') || block;
    var cta = makeResumeCta(ctaLabel());
    if (cta) host.appendChild(cta);
  }

  /* ---------- top alert ---------- */

  function removeTopAlert() {
    var bar = document.getElementById('tcc-no-order-alert');
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
  }

  function topAlert() {
    if (state === 'ok' || state === 'unknown') { removeTopAlert(); return; }
    var existing = document.getElementById('tcc-no-order-alert');
    if (existing && existing.getAttribute('data-state') === state) return;
    removeTopAlert();

    var top = portal.querySelector('.tcc-portal-top') || portal.firstElementChild;
    if (!top) return;

    var copy = COPY[state], tone = TONES[copy.tone];

    var bar = document.createElement('div');
    bar.id = 'tcc-no-order-alert';
    bar.setAttribute('data-state', state);
    bar.style.cssText = 'background:' + tone.bg + ';border:2px solid ' + tone.border + ';border-radius:12px;' +
      'padding:14px 18px;margin:14px 0;display:flex;gap:12px;align-items:flex-start;flex-wrap:wrap;';

    var badge = document.createElement('span');
    badge.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;' +
      'width:28px;height:28px;border-radius:50%;background:' + tone.badge + ';color:#fff;' +
      'font-size:17px;font-weight:700;line-height:1;flex-shrink:0;';
    badge.textContent = tone.glyph;

    var txt = document.createElement('div');
    txt.style.cssText = 'flex:1;min-width:220px;';
    var t = document.createElement('strong');
    t.textContent = copy.title;
    t.style.cssText = 'display:block;color:' + tone.head + ';margin-bottom:4px;';
    var p = document.createElement('p');
    p.textContent = copy.body;
    p.style.cssText = 'margin:0;color:' + tone.text + ';font-size:.9rem;line-height:1.5;';
    txt.appendChild(t);
    txt.appendChild(p);

    var line = contractLine();
    if (line) {
      var cl = document.createElement('p');
      cl.className = 'tcc-contract-line';
      cl.textContent = line;
      cl.style.cssText = 'margin:6px 0 0;color:' + tone.text + ';font-size:.76rem;opacity:.8;';
      txt.appendChild(cl);
    }

    bar.appendChild(badge);
    bar.appendChild(txt);
    if (wantsCta()) {
      var cta = makeResumeCta(ctaLabel());
      if (cta) { cta.style.marginTop = '0'; bar.appendChild(cta); }
    }

    top.parentNode.insertBefore(bar, top.nextSibling);
  }

  function sweep() {
    var locked = document.getElementById('tcc-locked-banner');
    applyBlock(locked);
    var suc = document.getElementById('tcc-success');
    if (suc && !suc.hasAttribute('hidden')) applyBlock(suc);
    else restoreBlock(suc);
    topAlert();
  }

  function startObserver() {
    if (observerStarted || !window.MutationObserver || !document.body) return;
    observerStarted = true;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; sweep(); });
    }).observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['hidden']
    });
  }

  /* ---------- order-presence check — ACTIVE only ---------- */

  // Same Sunday-11PM-ET rollover as tccWeekOfMonday_() in
  // snippets/tcc-dish-portal.liquid. Must stay identical — see that file's
  // header for why. Only needed here to key the /api/portal/status call.
  var ET_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit'
  });
  function etParts(d) {
    var p = {};
    ET_FMT.formatToParts(d).forEach(function (x) { p[x.type] = x.value; });
    var h = p.hour === '24' ? '0' : p.hour;
    return { year: +p.year, month: +p.month, day: +p.day, hour: +h };
  }
  function etDow(p) { return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay(); }
  function weekOfMonday() {
    var p = etParts(new Date()), dow = etDow(p), offset = -((dow + 6) % 7);
    if (dow === 0 && p.hour >= 23) offset = 1;
    var mon = new Date(Date.UTC(p.year, p.month - 1, p.day + offset));
    return mon.getUTCFullYear() + '-' + String(mon.getUTCMonth() + 1).padStart(2, '0') + '-' + String(mon.getUTCDate()).padStart(2, '0');
  }

  function resolveActiveState(res) {
    if (!res || res.status === 'error') return 'unknown';
    if (typeof res.has_order === 'undefined') return 'unknown';
    if (res.has_order === true) return 'ok';
    if (res.has_order !== false) return 'unknown';
    return res.confirmed ? 'pending_saved' : 'pending_pick';
  }

  function checkOrderStatus(force) {
    if (!email || inFlight) return;
    inFlight = true;
    var url = WORKER_URL + '/api/portal/status' +
      '?email=' + encodeURIComponent(email) +
      '&week_of=' + encodeURIComponent(weekOfMonday()) +
      '&_t=' + Date.now();
    fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        confirmed = !!(res && res.confirmed);
        var next = resolveActiveState(res);
        if (next !== state) { state = next; removeTopAlert(); }
        sweep();
        startObserver();
      })
      .catch(function (err) {
        console.error('[TCC] order status check failed:', err && err.message);
      })
      .then(function () { inFlight = false; });
  }

  /* ---------- entry point ---------- */

  // Every non-ACTIVE status renders from the tag alone and needs no order
  // lookup — the message doesn't depend on whether a row exists in Orders.
  var TAG_TO_STATE = {
    PAUSED: 'paused',
    CANCELLED: 'cancelled',
    FAILED: 'failed',
    EXPIRED: 'expired'
  };

  if (TAG_TO_STATE[tagStatus]) {
    state = TAG_TO_STATE[tagStatus];
    sweep();
    startObserver();
  } else if (tagStatus === 'ACTIVE') {
    // Only this branch needs the order-presence fetch.
    checkOrderStatus(true);
    setTimeout(function () { sweep(); }, 1500);
    setInterval(function () { checkOrderStatus(false); }, POLL_MS);
    window.addEventListener('focus', function () { checkOrderStatus(false); });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) checkOrderStatus(false);
    });
  }
  // else: no sub: tag matched (unknown/no contract on this customer record —
  // expected for logins under an unmerged duplicate account). state stays
  // 'unknown'. Nothing rendered, nothing fetched. This is the rule.
})();


/* ============================================================
 * [3] PORTAL DISH-COUNT ENFORCEMENT  (Aug 11 2026, rev 2)
 *
 * THE BUG BEING FIXED: snippets/tcc-dish-portal.liquid enables the Confirm
 * button with
 *   btn.disabled = n === 0;
 * so ANY selection of one or more dishes could be confirmed, even though the
 * note underneath correctly said "1 more to go". That is how Avin Patel
 * confirmed 4 dishes on a 5-meal plan (#3470) - the kitchen got a short order
 * and the missing dish had to be chased by hand.
 *
 * It should be `n !== MAX`. Fixing it in place would mean re-transmitting the
 * whole 74KB portal file, so this enforces the same rule from outside:
 *  - re-disables the button whenever the count is not exactly MAX
 *  - blocks the click in the CAPTURE phase as a second line of defence, in
 *    case the portal's own updateUI() re-enables it between our checks
 *
 * MAX and the live count both come from the portal's own DOM (data-max and
 * #tcc-sel-n), so this stays in step with whatever plan size it decided on -
 * including the v10 multi-subscription logic.
 *
 * ------------------------------------------------------------
 * ⚠️ REV 2 — WHY REV 1 FROZE THE PAGE (root cause, recorded)
 *
 * Rev 1 observed document.body with { childList, characterData, subtree } and
 * its callback wrote:
 *     note.textContent = (MAX - n) + ' more to go ...'
 * UNCONDITIONALLY. Assigning textContent replaces the child text node, which
 * is a childList mutation, which re-fires the observer, which rewrites it.
 * Observer callbacks are microtasks, so the queue never yields to paint:
 * "Page Unresponsive". It triggered the moment a customer picked their FIRST
 * dish (the write is gated on n > 0), i.e. immediately, every time.
 *
 * The `disabled` attribute was NOT the loop. `if (btn.disabled !== !ok)`
 * already made that write conditional, so it settled after one hop. Removing
 * 'disabled' from attributeFilter alone would have left the freeze in place.
 * It is dropped here anyway as dead weight - the childList mutation that
 * causes a re-enable is already observed, so nothing is lost.
 *
 * Rev 2 fixes:
 *   - every DOM write compares first and writes only on a real change
 *   - the observer callback is coalesced through requestAnimationFrame, so a
 *     future missed case throttles to one pass per frame instead of freezing
 *   - the note's red error colour is now cleared once the count is valid
 *     (rev 1 read the colour back as a state flag, so once it went red the
 *     countdown text was permanently frozen)
 * ------------------------------------------------------------ */
(function () {
  'use strict';

  var portal = document.getElementById('tcc-portal');
  if (!portal || portal.dataset.subscriber !== 'true') return;

  var MAX = parseInt(portal.dataset.max, 10) || 0;
  if (!MAX) return;

  var RED = '#C0392B';
  var noteState = '';

  function count() {
    var el = document.getElementById('tcc-sel-n');
    return el ? (parseInt(el.textContent, 10) || 0) : 0;
  }

  // The mandatory-bonus-dish rule was only ever built into the PRODUCT page's
  // Add to Cart. The portal's Confirm is a separate code path and never got it,
  // so a subscriber could confirm with the dropdown still on its placeholder.
  // Returns true when the bonus dish is either satisfied or not applicable.
  function bonusOk() {
    var sel = document.getElementById('tcc-bonus-select');
    if (!sel) return true;              // not offered to this customer
    if (!sel.offsetParent) return true; // container is display:none - not in play
    return !!(sel.value && sel.value.trim());
  }

  // Every write in here is conditional. See the rev 2 note above.
  function setNoteText(text) {
    var note = document.getElementById('tcc-confirm-note');
    if (note && note.textContent !== text) note.textContent = text;
  }

  function setNoteState(state) {
    if (state === noteState) return;
    var note = document.getElementById('tcc-confirm-note');
    if (!note) return;
    noteState = state;
    note.style.color = (state === 'error') ? RED : '';
  }

  function enforce() {
    var btn = document.getElementById('tcc-confirm-btn');
    if (!btn) return;
    // Leave it alone once a confirmation is in flight - the portal sets its
    // own label there ("Saving...", "Try again").
    if ((btn.textContent || '').trim().indexOf('Saving') === 0) return;

    var n = count();
    var dishesOk = (n === MAX);
    var ok = dishesOk && bonusOk();

    if (btn.disabled !== !ok) btn.disabled = !ok;

    if (ok) {
      // Clear our red override so the portal's own messaging renders normally.
      setNoteState('');
    } else if (noteState !== 'error') {
      if (!dishesOk && n > 0) {
        setNoteText((MAX - n) + ' more to go - please pick all ' + MAX + ' dishes to confirm');
      } else if (dishesOk) {
        setNoteText('Please choose your free bonus dish to confirm');
      }
    }
  }

  // Capture phase, so this runs before the portal's own onclick handler.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#tcc-confirm-btn') : null;
    if (!btn) return;
    var n = count();
    if (n !== MAX) {
      e.preventDefault();
      e.stopPropagation();
      setNoteState('error');
      setNoteText('Please pick all ' + MAX + ' dishes before confirming (' + n + ' selected).');
      btn.disabled = true;
      return;
    }
    if (!bonusOk()) {
      e.preventDefault();
      e.stopPropagation();
      setNoteState('error');
      setNoteText('Please choose your free bonus dish before confirming.');
      btn.disabled = true;
      var sel = document.getElementById('tcc-bonus-select');
      if (sel && sel.focus) { sel.focus(); sel.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
    }
  }, true);

  // A <select> changing value does NOT mutate the DOM, so the observer below
  // never sees it. Without this the button would stay stale for up to a second.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'tcc-bonus-select') { setNoteState(''); enforce(); }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforce);
  } else {
    enforce();
  }

  if (window.MutationObserver && document.body) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; enforce(); });
    }).observe(document.body, {
      childList: true, subtree: true, characterData: true
    });
  }

  setInterval(enforce, 1000);
})();


/* ============================================================
 * [4] RESUME SUBSCRIPTION BUTTON  (Aug 11 2026)
 *
 * THE BUG: "Manage Your Subscription" offers Skip / Pause / Cancel / History.
 * There is no Resume. A paused subscriber - which after the Monday billing
 * migration is a large slice of the base - has no way back in, and module [2]
 * tells them to "click Resume", which does not exist. The portal was naming an
 * action it did not provide.
 *
 * WHY NOT DETECT PAUSE STATE: we cannot. subscriptionContract and friends are
 * Access Denied for third-party tokens - an ownership boundary, not a scope we
 * can request - so the Worker has no way to know whether a contract is paused.
 * Conditional labelling is therefore impossible. Always showing Resume costs an
 * active subscriber nothing (Shopify's own page just shows no paused contract)
 * and unblocks everyone who is stuck.
 *
 * The href is CLONED from the existing Pause button rather than hardcoded, so
 * it always points at the same customer-account page and survives that UUID
 * changing.
 * ============================================================ */
(function () {
  'use strict';

  var portal = document.getElementById('tcc-portal');
  if (!portal || portal.dataset.subscriber !== 'true') return;

  function addResume() {
    if (document.getElementById('tcc-manage-btn-resume')) return; // idempotent
    var pause = document.querySelector('.tcc-manage-btn--pause');
    if (!pause || !pause.parentNode) return;

    var a = document.createElement('a');
    a.id = 'tcc-manage-btn-resume';
    a.className = 'tcc-manage-btn tcc-manage-btn--resume';
    a.href = pause.getAttribute('href');
    a.target = pause.getAttribute('target') || '_blank';
    a.rel = 'noopener';
    a.textContent = '\u25B6 Resume Subscription';
    a.style.borderColor = '#2E7D32';
    a.style.color = '#2E7D32';

    pause.parentNode.insertBefore(a, pause.nextSibling);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addResume);
  } else {
    addResume();
  }

  // The manage section renders with the rest of the portal, which may be after
  // this runs. Coalesced per the standing rule at the top of the file.
  if (window.MutationObserver && document.body) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; addResume(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
  setTimeout(addResume, 1500);
})();
